'use client';
import { API_ROUTES, HTTP_METHODS, useApiMutation } from '@shared/api-utils';
import { useSession } from '@shared/auth';
import { useDebug } from '@shared/debug';
import { type SportsbookSDKConfig, useSportsbookSDK } from '@shared/hooks';
import { useClientConfig } from '@shared/utils';
import { useTheme } from 'next-themes';
import { useEffect, useState } from 'react';
import { AltenarSDKProvider } from './AltenarSDKContext';

type AltenarFrameProps = {
  sdkConfig: SportsbookSDKConfig;
  culture: string;
  children: React.ReactNode;
};

/**
 * A component that initializes the Altenar SDK and fetches the Altenar session.
 * @param sdkConfig - The SDK configuration.
 * @param culture - The culture to use for the SDK (like 'en-GB).
 * @param children - The children components.
 * @returns A React component.
 */
function AltenarFrame({ sdkConfig, culture, children }: AltenarFrameProps) {
  const [altenarToken, setAltenarToken] = useState('');
  const { mutate: fetchAltenarSession } = useApiMutation<{ sessionId: string }, void>(API_ROUTES.SPORTSBOOK.LAUNCH, HTTP_METHODS.POST, {
    requestOptions: { credentials: true },
    mutationOptions: {
      onSuccess: data => {
        if (data?.sessionId) setAltenarToken(data.sessionId);
      },
    },
  });
  const debug = useDebug();
  const { resolvedTheme } = useTheme();
  const config = useClientConfig();
  const { isAuthenticated } = useSession();

  // The launch endpoint mints a player-tied Altenar session, so it is authenticated. Only fetch it
  // when logged in — otherwise it 401s (and cascades into a token refresh) for anonymous visitors,
  // who still get the widget without a session token.
  useEffect(() => {
    if (!isAuthenticated) return;
    fetchAltenarSession();
  }, [isAuthenticated, fetchAltenarSession]);

  const sdkParams = {
    integration: config.sportsbook.integration,
    culture,
    token: altenarToken,
    timezoneOffset: new Date().getTimezoneOffset(),
    oddsFormat: 1,
    themeName: resolvedTheme,
    ...(culture === 'en-GB' ? { numFormat: 'en-GB' } : {}),
  };

  const { initializeSDK, isLoaded } = useSportsbookSDK({
    params: sdkParams,
    config: sdkConfig,
    env: config.sportsbook.env,
  });

  useEffect(() => {
    initializeSDK().catch(error => {
      debug.error('Failed to initialize Alternar SDK:', error);
    });
  }, [initializeSDK, debug]);

  return <AltenarSDKProvider value={isLoaded}>{children}</AltenarSDKProvider>;
}

export { AltenarFrame };
